import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTable, useSortBy, useFilters, usePagination } from 'react-table';
import axios from 'axios';
import Layout from '../components/Layout'; // 引入通用 Layout 组件
import './ReviewPage.css';
import ReviewForm from '../components/ReviewForm'; // 引入 ReviewForm 组件
import { submitReview } from '../utils/api'; // 导入通用函数
import { allColumns } from '../config/columnsConfig'; // 引入共享的 allColumns 定义


const host = process.env.REACT_APP_API_HOST; // API 地址

function ReviewPage() {
    const [data, setData] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null); // 当前选中的审核项
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const userSettings = JSON.parse(localStorage.getItem('userSettings'));
        const tableSettings = userSettings?.tableSettings || {};

        // 确保 filters 中的列在 visibleColumns 中
        const filters = tableSettings.filters || [];
        const visibleColumnsSet = new Set(tableSettings?.visibleColumns || [
            'Query',
            'Answer',
            '审核状态',
            '审核操作',
        ]);
        filters.forEach((filter) => {
            if (!visibleColumnsSet.has(filter.id)) {
                visibleColumnsSet.add(filter.id);
            }
        });
        return Array.from(visibleColumnsSet);
    });
    // const [isColumnSettingsVisible, setIsColumnSettingsVisible] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        const fetchData = async () => {
            try {
                    const response = await axios.get(`${host}/api/data/chat_data_reviewed`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setData(response.data || []);
            } catch (error) {
                console.error('获取数据失败:', error.response?.data?.error);
                alert('获取数据失败，请稍后重试。');
                // navigate('/login')
            }
        };
        const fetchSettings = async () => {
            try {
                const userSettings = JSON.parse(localStorage.getItem('userSettings')) || {};
                const tableSettings = userSettings.tableSettings || {};

                // 确保 filters 中的列在 visibleColumns 中
                const filters = tableSettings.filters || [];
                const visibleColumnsSet = new Set(tableSettings?.visibleColumns || [
                    'Query',
                    'Answer',
                    '审核状态',
                    '审核操作',
                ]);
                filters.forEach((filter) => {
                    if (!visibleColumnsSet.has(filter.id)) {
                        visibleColumnsSet.add(filter.id);
                    }
                });

                const updatedVisibleColumns = Array.from(visibleColumnsSet);

                // 恢复表格设置
                setPageSize(tableSettings.pageSize || 5);
                gotoPage(tableSettings.pageIndex || 0);
                setAllFilters(filters); // 设置过滤器
                setSortBy(tableSettings.sortBy || []);
                setVisibleColumns(updatedVisibleColumns);

                // console.log('加载的 filters:', filters);
            } catch (error) {
                console.error('获取用户设置失败:', error.response?.data?.error || error);
            }
        };

        fetchSettings();
        fetchData();
    
    } , [navigate]);

    const handleEdit = useCallback((item) => {
        setSelectedItem(item); // 设置当前选中的审核项
    }, []);

    const handleSubmit = async (reviewData) => {
        const token = localStorage.getItem('token');
        try {
            await submitReview(reviewData, token); // 调用通用函数
            setData((prevData) =>
                prevData.map((item) =>
                    item.id === reviewData.id
? { ...item, Query: reviewData.Query, Answer: reviewData.Answer, review_conclusion: reviewData.review_conclusion }
: item
                )
            );
            alert('审核提交成功');
        } catch (error) {
            console.error('Error updating review:', error);
        }
        setSelectedItem(null); // 关闭审核弹窗
    };

    // const allColumns = useMemo(() => allColumns,[]);

    const columns = useMemo(
        () =>
            allColumns(handleEdit).filter((col) =>
                visibleColumns.includes( col.Header||col.accessor)
            ),
        [visibleColumns, handleEdit]
    );

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        []
    );

    const userSettings = JSON.parse(localStorage.getItem('userSettings')) || {};
    const tableSettings = userSettings.tableSettings || {
        pageIndex: 0,
        pageSize: 5,
        sortBy: [],
        filters: [],
        visibleColumns: visibleColumns,
    };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        setAllFilters,
        setSortBy,
        state: { pageIndex, pageSize, filters, sortBy },
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            initialState: {
                pageIndex: tableSettings.pageIndex || 0,
                pageSize: tableSettings.pageSize || 5,
                sortBy: tableSettings.sortBy || [],
                filters: tableSettings.filters || [],
            },
        },
        useFilters,
        useSortBy,
        usePagination
    );

    useEffect(() => {
        const tableSettings = {
            pageIndex,
            pageSize,
            sortBy,
            filters: filters.filter((filter) => visibleColumns.includes(filter.id)), // 仅保留 visibleColumns 中的过滤条件
            visibleColumns,
        };



        const saveSettingsToBackend = async () => {
            const token = localStorage.getItem('token');
            try {
                await axios.post(
                    `${host}/api/data/user_settings`,
                    {
                        user_id: localStorage.getItem('user_id'),
                        settings: userSettings,
                    },
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
            } catch (error) {
                console.error('保存用户设置失败:', error.response?.data?.error || error);
                alert('保存设置失败，请稍后重试。');
            }
        };
        const userSettings = JSON.parse(localStorage.getItem('userSettings')) || {};
        userSettings.tableSettings = tableSettings;
        // 避免重复保存到 localStorage
        const newSettings = JSON.stringify(userSettings);
        if (localStorage.getItem('userSettings') !== newSettings) {
            localStorage.setItem('userSettings', newSettings);
            saveSettingsToBackend();
        }
        
    }, [pageIndex, pageSize, sortBy, filters, visibleColumns]); // 确保依赖项正确

    return (
        <Layout>
            <div className="table-frame">
                <table {...getTableProps()} className="data-table">
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th key={column.id} {...column.getHeaderProps(column.getSortByToggleProps())}>
                                        {column.render('Header')}
                                        {column.canFilter && column.render('Filter') ? (
                                            <div>{column.render('Filter')}</div>
                                        ) : null}
                                        <span>
                                            {column.isSorted
                                                ? column.isSortedDesc
                                                    ? ' 🔽'
                                                    : ' 🔼'
                                                : ''}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row) => {
                            prepareRow(row);
                            return (
                                <tr key={row.id} {...row.getRowProps()}>
                                    {row.cells.map((cell) => (
                                        <td key={cell.column.id} {...cell.getCellProps()}>
                                            {cell.render('Cell')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* 分页控件 */}
                <div className="pagination">
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        {'<<'}
                    </button>
                    <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                        {'<'}
                    </button>
                    <button onClick={() => nextPage()} disabled={!canNextPage}>
                        {'>'}
                    </button>
                    <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                        {'>>'}
                    </button>
                    <span>
                        Page{' '}
                        <strong>
                            {pageIndex + 1} of {pageOptions.length}
                        </strong>{' '}
                    </span>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                        {[5, 10, 20, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* 审核表单 */}
            {selectedItem && (
                <ReviewForm
                    item={selectedItem}
                    onSubmit={handleSubmit}
                    onCancel={() => setSelectedItem(null)}
                />
            )}
        </Layout>
    );
}

// 默认列的筛选器
function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
    return (
        <input
            value={filterValue || ''}
            onChange={(e) => setFilter(e.target.value || undefined)}
            placeholder={`Search...`}
        />
    );
}

// 下拉筛选器
function SelectColumnFilter({ column: { filterValue, setFilter, preFilteredRows, id } }) {
    const options = React.useMemo(() => {
        const options = new Set();

        preFilteredRows.forEach((row) => {
                        const value = row.values[id];
            options.add(value);
        });
        return [...options.values()];
    }, [id, preFilteredRows]);

    return (
        <select
            value={filterValue}
            onChange={(e) => {
                const value = e.target.value;
                setFilter(value || undefined);
            }}
        >
            <option value="">All</option>
            {options.map((option, i) => (
                <option key={i} value={option}>
                    {option}
                </option>
            ))}
        </select>
    );
}

export default ReviewPage;